import { RECRUIT_LINK_WHEN_IMPERSONATING } from './alliance-apps';

/**
 * Who is signed in, normalized so the user control, the recruiting link and
 * the System Screens look the same in every app. Each repo builds this from
 * its own auth state (see hooks/use-alliance-identity.ts); these components
 * never read Cognito or /authorize directly.
 */

export interface AlliancePerson {
  /** "Jordan Ellis". Falls back to the username when no name is known */
  fullName: string;
  /** DMD number for agents (DMD + 6 digits), username for admins */
  id: string;
  email?: string | null;
}

type AllianceRole = 'agent' | 'admin' | 'staff';

export interface AllianceIdentity {
  role: AllianceRole;
  /** The signed-in account. For staff, the staff member themself */
  user: AlliancePerson;
  /** Staff only: the agent they are staff for (myStaffForAgt) */
  staffFor: AlliancePerson | null;
  /** The agent being impersonated, by an admin, an upline agent or staff */
  impersonating: AlliancePerson | null;
}

/** "Jordan Ellis" → "Jordan E." (a single word is returned as is) */
export const getShortName = (params: { fullName: string }) => {
  const words = params.fullName.trim().split(/\s+/);
  if (words.length < 2) {
    return params.fullName;
  }
  return `${words[0]} ${words[words.length - 1].charAt(0)}.`;
};

/** How the signed-in user relates to the agent a recruiting link is for */
export type RecruitRelation = 'self' | 'staff' | 'viewing' | 'downline';

/**
 * The agent a recruiting link is for, and how the user relates to them.
 * Admins who aren't impersonating get no link.
 *
 * While impersonating, there is no link until Contracting's
 * `/register/get-uuid` can return another agent's GUID (see
 * RECRUIT_LINK_WHEN_IMPERSONATING): today it would return the signed-in
 * user's own link.
 */
export const getRecruitTarget = (params: {
  identity: AllianceIdentity;
}): { agent: AlliancePerson; relation: RecruitRelation } | null => {
  // Params
  const { role, user, staffFor, impersonating } = params.identity;

  if (impersonating) {
    if (!RECRUIT_LINK_WHEN_IMPERSONATING) {
      return null;
    }
    let relation: 'viewing' | 'downline' = 'viewing';
    if (role === 'agent') {
      relation = 'downline';
    }
    return { agent: impersonating, relation };
  }
  if (role === 'staff' && staffFor) {
    return { agent: staffFor, relation: 'staff' };
  }
  if (role === 'agent') {
    return { agent: user, relation: 'self' };
  }
  return null;
};
